import React, { useState, useEffect, useCallback } from "react";
import { Menu, ImageIcon, Sparkles, Columns3, X } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import ChatThread from "../components/ChatThread";
import PromptComposer from "../components/PromptComposer";
import ImageStudio from "../components/ImageStudio";
import ComparePanel from "../components/ComparePanel";
import ModelSwitcher from "../components/ModelSwitcher";
import { api, API } from "../lib/api";
import { FALLBACK_MODELS, DEFAULT_FREE_MODEL } from "../constants/models";
import { useUpgrade } from "../context/UpgradeContext";

export default function ChatApp() {
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [conversations, setConversations] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_FREE_MODEL);
  const [mode, setMode] = useState("chat");
  const [compareMode, setCompareMode] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isPro, openUpgrade } = useUpgrade();

  const streamingModel = models.find((m) => m.key === selectedModel);

  const sortConvs = (list) =>
    [...list].sort((a, b) => (a.pinned === b.pinned ? new Date(b.updated_at) - new Date(a.updated_at) : a.pinned ? -1 : 1));

  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get("/conversations");
      setConversations(res.data);
    } catch (e) {}
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const res = await api.get("/folders");
      setFolders(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    loadConversations();
    loadFolders();
  }, [loadConversations, loadFolders]);

  const loadMessages = async (convId, conv) => {
    try {
      const res = await api.get(`/conversations/${convId}/messages`);
      setMessages(res.data);
      if (conv) {
        setSelectedModel(conv.model || "gpt-5.4");
        setMode(conv.mode || "chat");
      }
    } catch (e) {
      setMessages([]);
    }
  };

  const selectConversation = async (convId) => {
    const conv = conversations.find((c) => c.conversation_id === convId);
    setCompareMode(false);
    setActiveId(convId);
    setSidebarOpen(false);
    await loadMessages(convId, conv);
  };

  const newChat = () => {
    setCompareMode(false);
    setActiveId(null);
    setMessages([]);
    setMode("chat");
    setSidebarOpen(false);
  };

  const newImage = () => {
    setCompareMode(false);
    setActiveId(null);
    setMessages([]);
    setMode("image");
    setSidebarOpen(false);
  };

  const ensureConversation = async (forMode) => {
    if (activeId) return activeId;
    const res = await api.post("/conversations", {
      title: forMode === "image" ? "New image" : "New chat",
      model: selectedModel,
      mode: forMode,
    });
    const conv = res.data;
    setConversations((prev) => [conv, ...prev]);
    setActiveId(conv.conversation_id);
    return conv.conversation_id;
  };

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");

    const convId = await ensureConversation("chat");
    const userMsg = { message_id: `local_${Date.now()}`, role: "user", content };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);
    setStreamingText("");

    let acc = "";
    let finalTitle = null;
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, model: selectedModel, content }),
      });
      if (resp.status === 402) {
        const j = await resp.json().catch(() => ({}));
        openUpgrade(j.detail || "Upgrade to OmniAI Pro to continue.");
        setMessages((prev) => prev.filter((m) => m.message_id !== userMsg.message_id));
        return;
      }
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          const data = JSON.parse(line);
          if (data.delta) {
            acc += data.delta;
            setStreamingText(acc);
          } else if (data.error) {
            toast.error("Model error: " + data.error);
          } else if (data.done) {
            finalTitle = data.title;
            setMessages((prev) => [...prev, { message_id: data.message_id, role: "assistant", content: acc, model: selectedModel }]);
          }
        }
      }
    } catch (e) {
      toast.error("Failed to get response. Please try again.");
    } finally {
      setStreaming(false);
      setStreamingText("");
      setConversations((prev) => sortConvs(prev.map((c) =>
        c.conversation_id === convId
          ? { ...c, updated_at: new Date().toISOString(), model: selectedModel, ...(finalTitle ? { title: finalTitle } : {}) }
          : c
      )));
    }
  };

  const generateImage = async (prompt) => {
    if (streaming) return;
    const convId = await ensureConversation("image");
    const localId = `local_${Date.now()}`;
    setMessages((prev) => [...prev, { message_id: localId, role: "user", content: prompt }]);
    setStreaming(true);
    setStreamingText("");
    try {
      const res = await api.post("/images/generate", { conversation_id: convId, prompt });
      setMessages((prev) => [...prev, res.data]);
      toast.success("Image generated");
      setConversations((prev) => sortConvs(prev.map((c) => (c.conversation_id === convId ? { ...c, mode: "image", updated_at: new Date().toISOString() } : c))));
    } catch (e) {
      if (e?.response?.status === 402) openUpgrade(e.response.data.detail);
      else toast.error("Image generation failed. Please try again.");
      setMessages((prev) => prev.filter((m) => m.message_id !== localId));
    } finally {
      setStreaming(false);
    }
  };

  const regenerate = (assistantMsg) => {
    const idx = messages.findIndex((m) => m.message_id === assistantMsg.message_id);
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === "user") { sendMessage(messages[i].content); return; }
    }
  };

  const renameConversation = async (id, title) => {
    setConversations((prev) => prev.map((c) => (c.conversation_id === id ? { ...c, title } : c)));
    try { await api.patch(`/conversations/${id}`, { title }); } catch (e) {}
  };

  const deleteConversation = async (id) => {
    setConversations((prev) => prev.filter((c) => c.conversation_id !== id));
    if (activeId === id) newChat();
    try { await api.delete(`/conversations/${id}`); toast.success("Chat deleted"); } catch (e) {}
  };

  const pinConversation = async (id) => {
    try {
      const res = await api.post(`/conversations/${id}/pin`);
      setConversations((prev) => sortConvs(prev.map((c) => (c.conversation_id === id ? { ...c, pinned: res.data.pinned } : c))));
    } catch (e) {}
  };

  const moveConversation = async (id, folderId) => {
    setConversations((prev) => prev.map((c) => (c.conversation_id === id ? { ...c, folder_id: folderId } : c)));
    try {
      await api.patch(`/conversations/${id}/folder`, { folder_id: folderId });
      toast.success(folderId ? "Moved to folder" : "Removed from folder");
    } catch (e) {}
  };

  const createFolder = async (name) => {
    try {
      const res = await api.post("/folders", { name });
      setFolders((prev) => [...prev, res.data]);
      toast.success("Folder created");
    } catch (e) {}
  };

  const renameFolder = async (id, name) => {
    setFolders((prev) => prev.map((f) => (f.folder_id === id ? { ...f, name } : f)));
    try { await api.patch(`/folders/${id}`, { name }); } catch (e) {}
  };

  const deleteFolder = async (id) => {
    setFolders((prev) => prev.filter((f) => f.folder_id !== id));
    setConversations((prev) => prev.map((c) => (c.folder_id === id ? { ...c, folder_id: null } : c)));
    try { await api.delete(`/folders/${id}`); toast.success("Folder deleted"); } catch (e) {}
  };

  const sidebarProps = {
    conversations, folders, activeId,
    onSelect: selectConversation, onNewChat: newChat, onNewImage: newImage,
    onRename: renameConversation, onDelete: deleteConversation, onPin: pinConversation,
    onMove: moveConversation, onCreateFolder: createFolder, onRenameFolder: renameFolder, onDeleteFolder: deleteFolder,
  };

  return (
    <div className="h-screen w-full flex overflow-hidden bg-[#F9F9F7]">
      {/* Desktop sidebar */}
      <div className="hidden lg:block w-[280px] shrink-0">
        <Sidebar {...sidebarProps} />
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-[280px] max-w-[80%]">
            <Sidebar {...sidebarProps} onClose={() => setSidebarOpen(false)} />
          </div>
          <div className="flex-1 bg-black/30" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 border-b border-[#E6E6DF] bg-[#F9F9F7]/80 backdrop-blur-md" data-testid="model-switcher-header">
          <button className="lg:hidden text-[#5F5F5B] p-1" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
            <Menu className="h-5 w-5" />
          </button>
          {compareMode ? (
            <div className="flex items-center gap-2 rounded-full border border-[#CFE3D8] bg-[#EAF3EE] px-3.5 py-2" data-testid="compare-mode-indicator">
              <Columns3 className="h-4 w-4 text-[#2D6A4F]" />
              <span className="text-sm font-semibold text-[#1F4A38]">Compare Models</span>
            </div>
          ) : mode === "image" ? (
            <div className="flex items-center gap-2 rounded-full border border-[#AECBFA] bg-[#E8F0FE] px-3.5 py-2" data-testid="image-mode-indicator">
              <ImageIcon className="h-4 w-4 text-[#1A73E8]" />
              <span className="text-sm font-semibold text-[#174EA6]">Image Studio</span>
            </div>
          ) : (
            <ModelSwitcher models={models} value={selectedModel} onChange={(k) => setSelectedModel(k)} />
          )}
          <div className="flex-1" />
          {compareMode ? (
            <button onClick={() => { setCompareMode(false); newChat(); }} className="flex items-center gap-2 rounded-full border border-[#E6E6DF] bg-white px-3.5 py-2 text-sm font-medium text-[#5F5F5B] hover:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }} data-testid="exit-compare-button">
              <X className="h-4 w-4" /> <span className="hidden sm:inline">Exit compare</span>
            </button>
          ) : (
            <>
              <button onClick={() => (isPro ? setCompareMode(true) : openUpgrade("Compare answers from three models side by side."))} className="flex items-center gap-2 rounded-full border border-[#E6E6DF] bg-white px-3.5 py-2 text-sm font-medium text-[#5F5F5B] hover:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }} data-testid="compare-button">
                <Columns3 className="h-4 w-4 text-[#2D6A4F]" /> <span className="hidden sm:inline">Compare</span>
              </button>
              <button onClick={() => { if (mode === "image") { newChat(); } else if (isPro) { newImage(); } else { openUpgrade("Image generation is a Pro feature."); } }} className="flex items-center gap-2 rounded-full border border-[#E6E6DF] bg-white px-3.5 py-2 text-sm font-medium text-[#5F5F5B] hover:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }} data-testid="toggle-mode-button">
                {mode === "image" ? <Sparkles className="h-4 w-4" /> : <ImageIcon className="h-4 w-4 text-[#1A73E8]" />}
                <span className="hidden sm:inline">{mode === "image" ? "Text chat" : "Image studio"}</span>
              </button>
            </>
          )}
        </header>

        {compareMode ? (
          <ComparePanel models={models} onExit={() => setCompareMode(false)} />
        ) : (
          <>
            <ChatThread
              messages={messages}
              models={models}
              streaming={streaming}
              streamingText={streamingText}
              streamingModel={mode === "image" ? { family: "gemini", label: "Gemini Image" } : streamingModel}
              onRegenerate={mode === "image" ? null : regenerate}
              onPreset={(p) => (mode === "image" ? generateImage(p) : sendMessage(p))}
            />
            {mode === "image" ? (
              <ImageStudio onGenerate={generateImage} disabled={streaming} />
            ) : (
              <PromptComposer value={input} onChange={setInput} onSend={() => sendMessage()} disabled={streaming} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
